/**
 * DDY Parser Service
 *
 * Parses EnergyPlus Design Day (DDY) files and extracts design day data.
 * DDY files are in standard IDF format containing SizingPeriod:DesignDay objects.
 */

import { parseIDF } from '../../parser/parser';
import { serializeObject } from '../../parser/serializer';
import type { IDFObject } from '../../parser/types';
import type { DesignDayMetadata, ParsedDesignDay, DesignDayType, DesignDayCategory } from '../types';

/**
 * SizingPeriod:DesignDay field indices (based on EnergyPlus IDD).
 */
const FIELD_INDICES = {
  name: 0,
  month: 1,
  dayOfMonth: 2,
  dayType: 3,
  maxDryBulbTemp: 4,
  dailyDryBulbTempRange: 5,
  dryBulbTempRangeModifierType: 6,
  dryBulbTempRangeModifierSchedule: 7,
  humidityConditionType: 8,
  wetbulbOrDewPoint: 9,
  humidityConditionDaySchedule: 10,
  humidityRatioAtMaxDryBulb: 11,
  enthalpyAtMaxDryBulb: 12,
  dailyWetBulbTempRange: 13,
  barometricPressure: 14,
  windSpeed: 15,
  windDirection: 16,
  rainIndicator: 17,
  snowIndicator: 18,
  daylightSavingTimeIndicator: 19,
  solarModelIndicator: 20,
  beamSolarDaySchedule: 21,
  diffuseSolarDaySchedule: 22,
  ashraeClearSkyOpticalDepthForBeam: 23,
  ashraeClearSkyOpticalDepthForDiffuse: 24,
  skyClearness: 25,
};

/**
 * Field name mappings for user-friendly display.
 */
const FIELD_NAMES: Record<number, string> = {
  [FIELD_INDICES.name]: 'Name',
  [FIELD_INDICES.month]: 'Month',
  [FIELD_INDICES.dayOfMonth]: 'Day of Month',
  [FIELD_INDICES.dayType]: 'Day Type',
  [FIELD_INDICES.maxDryBulbTemp]: 'Maximum Dry-Bulb Temperature',
  [FIELD_INDICES.dailyDryBulbTempRange]: 'Daily Dry-Bulb Temperature Range',
  [FIELD_INDICES.dryBulbTempRangeModifierType]: 'Dry-Bulb Temperature Range Modifier Type',
  [FIELD_INDICES.dryBulbTempRangeModifierSchedule]: 'Dry-Bulb Temperature Range Modifier Day Schedule Name',
  [FIELD_INDICES.humidityConditionType]: 'Humidity Condition Type',
  [FIELD_INDICES.wetbulbOrDewPoint]: 'Wetbulb or DewPoint at Maximum Dry-Bulb',
  [FIELD_INDICES.humidityConditionDaySchedule]: 'Humidity Condition Day Schedule Name',
  [FIELD_INDICES.humidityRatioAtMaxDryBulb]: 'Humidity Ratio at Maximum Dry-Bulb',
  [FIELD_INDICES.enthalpyAtMaxDryBulb]: 'Enthalpy at Maximum Dry-Bulb',
  [FIELD_INDICES.dailyWetBulbTempRange]: 'Daily Wet-Bulb Temperature Range',
  [FIELD_INDICES.barometricPressure]: 'Barometric Pressure',
  [FIELD_INDICES.windSpeed]: 'Wind Speed',
  [FIELD_INDICES.windDirection]: 'Wind Direction',
  [FIELD_INDICES.rainIndicator]: 'Rain Indicator',
  [FIELD_INDICES.snowIndicator]: 'Snow Indicator',
  [FIELD_INDICES.daylightSavingTimeIndicator]: 'Daylight Saving Time Indicator',
  [FIELD_INDICES.solarModelIndicator]: 'Solar Model Indicator',
  [FIELD_INDICES.beamSolarDaySchedule]: 'Beam Solar Day Schedule Name',
  [FIELD_INDICES.diffuseSolarDaySchedule]: 'Diffuse Solar Day Schedule Name',
  [FIELD_INDICES.ashraeClearSkyOpticalDepthForBeam]: 'ASHRAE Clear Sky Optical Depth for Beam Irradiance',
  [FIELD_INDICES.ashraeClearSkyOpticalDepthForDiffuse]: 'ASHRAE Clear Sky Optical Depth for Diffuse Irradiance',
  [FIELD_INDICES.skyClearness]: 'Sky Clearness',
};

/**
 * Field units mappings.
 */
const FIELD_UNITS: Record<number, string> = {
  [FIELD_INDICES.maxDryBulbTemp]: '°C',
  [FIELD_INDICES.dailyDryBulbTempRange]: '°C',
  [FIELD_INDICES.wetbulbOrDewPoint]: '°C',
  [FIELD_INDICES.humidityRatioAtMaxDryBulb]: 'kgWater/kgDryAir',
  [FIELD_INDICES.enthalpyAtMaxDryBulb]: 'J/kg',
  [FIELD_INDICES.dailyWetBulbTempRange]: '°C',
  [FIELD_INDICES.barometricPressure]: 'Pa',
  [FIELD_INDICES.windSpeed]: 'm/s',
  [FIELD_INDICES.windDirection]: 'deg',
};

/**
 * Classify design day type based on name and day type.
 */
function classifyDesignDayType(name: string, dayType: string): DesignDayType {
  const nameLower = name.toLowerCase();
  const dayTypeLower = dayType.toLowerCase();

  // Check for heating conditions
  if (
    nameLower.includes('htg') ||
    nameLower.includes('heating') ||
    nameLower.includes('humidification') ||
    dayTypeLower === 'winterdesignday'
  ) {
    if (nameLower.includes('humidification')) {
      return 'humidification';
    }
    return 'heating';
  }

  // Check for cooling conditions
  if (
    nameLower.includes('clg') ||
    nameLower.includes('cooling') ||
    nameLower.includes('dehumidification') ||
    dayTypeLower === 'summerdesignday'
  ) {
    if (nameLower.includes('dehumidification')) {
      return 'dehumidification';
    }
    return 'cooling';
  }

  // Check for extreme conditions
  if (
    nameLower.includes('extreme') ||
    nameLower.includes('min db') ||
    nameLower.includes('max db') ||
    nameLower.includes('max wb')
  ) {
    return 'extreme';
  }

  return 'other';
}

/**
 * Classify design day category based on name.
 */
function classifyDesignDayCategory(name: string): DesignDayCategory {
  const nameLower = name.toLowerCase();

  if (nameLower.includes('extreme')) {
    return 'extreme';
  }

  if (nameLower.includes('ann') || nameLower.includes('annual')) {
    return 'annual';
  }

  // Check for month names
  const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  for (const month of months) {
    if (nameLower.includes(month)) {
      return 'monthly';
    }
  }

  return 'annual';
}

/**
 * Generate a unique ID for a design day within a weather file.
 */
function generateDesignDayId(name: string, index: number): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 40);
  return `ddy-${index}-${slug}`;
}

/**
 * Parse a single SizingPeriod:DesignDay object into metadata.
 */
function parseDesignDayObject(obj: IDFObject, index: number): DesignDayMetadata {
  const getValue = (fieldIndex: number): string => {
    return obj.fields[fieldIndex]?.value ?? '';
  };

  const getNumber = (fieldIndex: number): number => {
    const val = getValue(fieldIndex);
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  const name = getValue(FIELD_INDICES.name);
  const dayType = getValue(FIELD_INDICES.dayType);
  const type = classifyDesignDayType(name, dayType);
  const category = classifyDesignDayCategory(name);

  const metadata: DesignDayMetadata = {
    id: generateDesignDayId(name, index),
    name,
    type,
    category,
    month: getNumber(FIELD_INDICES.month),
    dayOfMonth: getNumber(FIELD_INDICES.dayOfMonth),
    dayType,
    dryBulbTemp: getNumber(FIELD_INDICES.maxDryBulbTemp),
  };

  // Add optional fields if present
  const humidityType = getValue(FIELD_INDICES.humidityConditionType);
  if (humidityType.toLowerCase() === 'wetbulb') {
    metadata.wetBulbTemp = getNumber(FIELD_INDICES.wetbulbOrDewPoint);
  }

  const windSpeed = getNumber(FIELD_INDICES.windSpeed);
  if (windSpeed > 0) {
    metadata.windSpeed = windSpeed;
  }

  const windDir = getNumber(FIELD_INDICES.windDirection);
  if (windDir > 0) {
    metadata.windDirection = windDir;
  }

  return metadata;
}

/**
 * Parse a single SizingPeriod:DesignDay object into full parsed data.
 */
function parseDesignDayObjectFull(obj: IDFObject, index: number): ParsedDesignDay {
  const metadata = parseDesignDayObject(obj, index);

  // Serialize back to IDF format
  const idfObject = serializeObject(obj);

  // Extract field values with names
  const fields: Array<{ name: string; value: string; units?: string }> = obj.fields.map((field, i) => {
    const entry: { name: string; value: string; units?: string } = {
      name: FIELD_NAMES[i] ?? `Field ${i + 1}`,
      value: field.value,
    };
    const unit = FIELD_UNITS[i];
    if (unit !== undefined) {
      entry.units = unit;
    }
    return entry;
  });

  return {
    ...metadata,
    idfObject,
    fields,
  };
}

/**
 * Parse DDY file content and extract design day metadata (for indexing).
 * Returns only metadata without full IDF objects.
 */
export function parseDdyMetadata(content: string): DesignDayMetadata[] {
  const result = parseIDF(content);
  const designDays: DesignDayMetadata[] = [];

  let index = 0;
  for (const obj of result.document.objects) {
    if (obj.className.toLowerCase() === 'sizingperiod:designday') {
      designDays.push(parseDesignDayObject(obj, index));
      index++;
    }
  }

  return designDays;
}

/**
 * Parse DDY file content and extract full design day data (for on-demand fetching).
 * Returns complete data including IDF objects.
 */
export function parseDdyFull(content: string): ParsedDesignDay[] {
  const result = parseIDF(content);
  const designDays: ParsedDesignDay[] = [];

  let index = 0;
  for (const obj of result.document.objects) {
    if (obj.className.toLowerCase() === 'sizingperiod:designday') {
      designDays.push(parseDesignDayObjectFull(obj, index));
      index++;
    }
  }

  return designDays;
}

/**
 * Get recommended design days for typical sizing.
 * Returns IDs of recommended heating and cooling design days.
 */
export function getRecommendedDesignDays(designDays: DesignDayMetadata[]): string[] {
  const recommended: string[] = [];

  // Find heating 99.6% design day (most conservative for heating)
  const heating99_6 = designDays.find(
    (dd) =>
      dd.type === 'heating' &&
      dd.category === 'annual' &&
      dd.name.toLowerCase().includes('99.6')
  );
  if (heating99_6) {
    recommended.push(heating99_6.id);
  } else {
    // Fallback to any heating design day
    const anyHeating = designDays.find((dd) => dd.type === 'heating' && dd.category === 'annual');
    if (anyHeating) {
      recommended.push(anyHeating.id);
    }
  }

  // Find cooling 0.4% or 1% design day (most conservative for cooling)
  const cooling0_4 = designDays.find(
    (dd) =>
      dd.type === 'cooling' &&
      dd.category === 'annual' &&
      (dd.name.toLowerCase().includes('.4%') || dd.name.toLowerCase().includes('0.4%'))
  );
  if (cooling0_4) {
    recommended.push(cooling0_4.id);
  } else {
    // Fallback to 1% cooling
    const cooling1 = designDays.find(
      (dd) =>
        dd.type === 'cooling' &&
        dd.category === 'annual' &&
        dd.name.toLowerCase().includes('1%')
    );
    if (cooling1) {
      recommended.push(cooling1.id);
    } else {
      // Fallback to any cooling design day
      const anyCooling = designDays.find((dd) => dd.type === 'cooling' && dd.category === 'annual');
      if (anyCooling) {
        recommended.push(anyCooling.id);
      }
    }
  }

  return recommended;
}

/**
 * Group design days by type for display.
 * Works with both DesignDayMetadata and ParsedDesignDay.
 */
export function groupDesignDaysByType<T extends DesignDayMetadata>(
  designDays: T[]
): Record<DesignDayType, T[]> {
  const groups: Record<DesignDayType, T[]> = {
    heating: [],
    cooling: [],
    extreme: [],
    dehumidification: [],
    humidification: [],
    other: [],
  };

  for (const dd of designDays) {
    groups[dd.type].push(dd);
  }

  return groups;
}
